(ns minorodata.components.home-dashboard.hero
  (:require
   ["lucide-react" :refer [ChevronsDownUpIcon ChevronsUpDownIcon Send]]
   [clojure.string :as str]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.app.posthog-events :as posthog-events]
   [re-posh.core :as re-posh]
   [reagent.core :as reagent]
   [ui.button :as button]
   [ui.typography :as typography]
   [ui.utils :refer [clsx]]))

(def hero-suggestions
  ["Show failures from last 24hrs"
   "Explain SQL"
   "Suggest transform improvements"
   "Create a report"])

(def kai-expanded-storage-key "overview-kai-expanded")

(defn- stored-kai-expanded? []
  (let [value (js/JSON.parse (js/localStorage.getItem kai-expanded-storage-key))]
    (if (nil? value) true (boolean value))))

(defn open-kai-with-prompt [prompt]
  (let [trimmed (str/trim prompt)]
    (when (seq trimmed)
      (dispatch [:posthog/capture {:event (:overview/action-clicked posthog-events/events)
                                   :props {:action "ask_kai"}}])
      (dispatch [:ai-chat/set-pending-prompt trimmed])
      (dispatch [:modals.events/toggle-modal "ai-modal" true]))))

(defn overview-hero []
  (let [prompt        (reagent/atom "")
        expanded?     (reagent/atom (stored-kai-expanded?))
        set-expanded! (fn [value]
                        (reset! expanded? value)
                        (dispatch [:app.events/set-local-storage kai-expanded-storage-key value])
                        (dispatch [:posthog/set-person-properties {:overview-kai-expanded value}]))]
    (dispatch [:ai-chat/get-settings])

    (fn []
      (let [ai-settings @(re-posh/subscribe [:ai-settings/pull])
            ;; Assume enabled until settings load to avoid briefly flashing
            ;; the disabled state.
            enabled?    (not (false? (:ai-settings/assistant-enabled ai-settings)))

            submit-prompt
            (fn [e]
              (.preventDefault e)
              (let [trimmed (str/trim @prompt)]
                (when (and enabled? (seq trimmed))
                  (open-kai-with-prompt trimmed)
                  (reset! prompt ""))))

            select-suggestion
            (fn [suggestion]
              (when enabled?
                (open-kai-with-prompt suggestion)))

            prompt-input
            (fn [placeholder]
              [:div
               {:class (str
                        "flex w-full items-center gap-1 rounded-md border "
                        "border-kleene-neutral-01 bg-white p-1 shadow-sm "
                        "dark:border-kleene-dark-04 dark:bg-kleene-dark-02 "
                        "focus-within:border-kleene-secondary-v2 "
                        "focus-within:ring-1 focus-within:ring-kleene-secondary-v2")}

               [:input
                {:type        "text"
                 :value       @prompt
                 :placeholder placeholder
                 :aria-label  "Ask Kai"
                 :disabled    (not enabled?)
                 :on-change   #(reset! prompt (.. % -target -value))
                 :class       (str
                               "h-8 min-w-0 flex-1 border-0 bg-transparent "
                               "px-3 text-sm text-kleene-text-primary "
                               "placeholder:text-kleene-text-secondary "
                               "outline-none ring-0 focus:ring-0 "
                               "disabled:cursor-not-allowed")}]

               (when enabled?
                 [button/button
                  {:type       "submit"
                   :size       :icon-md
                   :icon       Send
                   :icon-size  22
                   :aria-label "Send prompt"
                   :disabled   (str/blank? @prompt)
                   :class      (str
                                "kai-accent-button "
                                "h-10 w-10 shrink-0 rounded-md "
                                "[&>div>span>svg]:stroke-2")}])])]

        [:section
         {:class "relative w-full overflow-hidden bg-transparent"}

         ;; Hero background squares, shared by enabled and disabled Kai.
         [:div
          {:aria-hidden true
           :class (str
                   "background-squares overview-background-squares "
                   "pointer-events-none absolute inset-0 z-0 opacity-60 "
                   "dark:opacity-70")}]

         (if @expanded?
           ;; Expanded state, shared by enabled and disabled Kai.
           [:div
            {:class "relative z-10 flex flex-col px-6 py-6 md:px-10 md:py-8 lg:px-12 lg:py-10"}

            [:div {:class "flex justify-end"}
             [button/button
              {:type          "button"
               :variant       :secondary
               :size          :small
               :icon          ChevronsDownUpIcon
               :aria-expanded true
               :on-click      #(set-expanded! false)}
              "Collapse"]]

            [:div
             {:class (str
                      "mx-auto flex w-full max-w-5xl flex-col items-center "
                      "justify-center gap-6 pb-6 pt-4 text-center "
                      "md:flex-row md:gap-8 md:pb-8 md:pt-2 md:text-left")}

             [:img
              {:src   "/img/kai/KAI-2.svg"
               :alt   "KAI"
               :class (clsx
                       "h-24 w-24 shrink-0 md:h-32 md:w-32"
                       {"grayscale opacity-70" (not enabled?)})}]

             [:div {:class "min-w-0"}
              [typography/h1
               "Hello, I'm "
               [:span
                {:class (if enabled?
                          "kai-accent-text"
                          "text-kleene-text-secondary")}
                "K"]
               "ai — the Kleene AI agent."]

              [typography/p
               {:class "mt-2 text-kleene-text-secondary"}
               (if enabled?
                 "How can I assist you today?"
                 "Unfortunately, I can't help you today.")]]]

            (if enabled?
              [:form
               {:class     "mx-auto w-full max-w-5xl"
                :on-submit submit-prompt}

               [prompt-input "Ask me anything or pick a suggestion below"]

               [:div {:class "mt-2 flex flex-wrap justify-center gap-2"}
                (for [suggestion hero-suggestions]
                  ^{:key suggestion}
                  [button/button
                   {:type     "button"
                    :variant  :secondary
                    :size     :small
                    :class    (str
                               "border-kleene-secondary-01 "
                               "text-kleene-secondary-01 "
                               "hover:bg-kleene-secondary-01/10 "
                               "dark:border-kleene-secondary-v2 "
                               "dark:text-kleene-secondary-v2 "
                               "dark:hover:bg-kleene-secondary-v2/10")
                    :on-click #(select-suggestion suggestion)}
                   suggestion])]]

              [:div {:class "mx-auto w-full max-w-5xl"}
               [prompt-input
                "Ask your admin to enable Kai in the settings page"]])]

           ;; Collapsed state, shared by enabled and disabled Kai.
           [:div
            {:class "relative z-10 flex w-full items-center gap-3 px-6 py-4 md:px-10"}

            [:img
             {:src   "/img/kai/KAI-2.svg"
              :alt   "KAI"
              :class (clsx
                      "h-12 w-12 shrink-0"
                      {"grayscale opacity-70" (not enabled?)})}]

            [:form
             {:class     "flex min-w-0 flex-1 items-center"
              :on-submit submit-prompt}
             [prompt-input
              (if enabled?
                "Ask Kai"
                "Unfortunately, I can't help you today. Ask your admin to enable Kai in the settings page")]]

            [button/button
             {:type          "button"
              :variant       :secondary
              :size          :small
              :icon          ChevronsUpDownIcon
              :aria-expanded false
              :class         "shrink-0"
              :on-click      #(set-expanded! true)}
             "Expand"]])]))))
